import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../auth/auth.service';
import { catchError, throwError, switchMap, BehaviorSubject, filter, take } from 'rxjs';

let isRefreshing = false;
let refreshTokenSubject = new BehaviorSubject<string | null>(null);

/**
 * Interceptor funcional: manejo global de errores HTTP.
 * - 401 → Intenta refrescar el token. Si falla, logout + redirect a login
 * - 403 → redirect a dashboard (sin permiso)
 * - 429 → loguea rate limit
 * - 5xx → propaga para que el componente lo maneje
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const auth   = inject(AuthService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      // Si es un error 401 y no es en los endpoints de login/refresh, intentamos refrescar
      if (err.status === 401 && !req.url.includes('/login') && !req.url.includes('/refresh')) {
        if (!isRefreshing) {
          isRefreshing = true;
          refreshTokenSubject.next(null);

          return auth.refreshToken().pipe(
            switchMap((tokenResponse) => {
              isRefreshing = false;
              refreshTokenSubject.next(tokenResponse.accessToken);
              
              // Reintentar la petición original con el nuevo token
              const authReq = req.clone({
                setHeaders: { Authorization: `Bearer ${tokenResponse.accessToken}` }
              });
              return next(authReq);
            }),
            catchError((refreshErr) => {
              isRefreshing = false;
              auth.logout();
              router.navigate(['/auth/login']);
              return throwError(() => refreshErr);
            })
          );
        } else {
          // Si ya se está refrescando, esperamos a que termine
          return refreshTokenSubject.pipe(
            filter(token => token !== null),
            take(1),
            switchMap(token => {
              const authReq = req.clone({
                setHeaders: { Authorization: `Bearer ${token}` }
              });
              return next(authReq);
            })
          );
        }
      }

      // Manejo estándar de otros errores (o si falló el refresh)
      switch (err.status) {
        case 401:
          auth.logout();
          router.navigate(['/auth/login']);
          break;
        case 403:
          console.warn('[HTTP 403] Sin permiso para:', req.url);
          router.navigate(['/dashboard']);
          break;
        case 429:
          console.warn('[HTTP 429] Rate limit excedido. Reintentar en 60s.');
          break;
        default:
          console.error('[HTTP Error]', err.status, req.url, err.message);
      }
      return throwError(() => err);
    })
  );
};
